// @flow

import React from "react";

import EditableForm from "../../components/EditableForm";
import FormRow from "../../components/FormRow";
import ConditionalSubform from "../../components/ConditionalSubform";
import EditableField from "../../components/EditableField";
import * as formConditions from "../../utils/formConditions";
import * as mappers from "../../utils/mappers";
import * as formatters from "../../utils/formatters";
import * as v from "../../utils/validators";
import { errors } from "../../utils/errors";

import {
  FINANCING_PRODUCT,
  LOAN_KIND,
  LOAN_TERM_IN_MONTHS,
  LOAN_FIXED_INTEREST_RATE,
  LOAN_FIXED_INTEREST_RATE__FIXED,
  CONSIDER_SUBSIDIES,
  LOAN_INTERESTED_IN_ALTERNATIVE_FINANCING,
  LOAN_INTEREST_PERIOD_IN_MONTHS,
  AMOUNT,
  FINANCING_OWN_FOUNDS,
  FINANCING_DEMAND,
  FINANCING_SUPPORT,
  LOAN_KIND__ANNUITY_LOAN
} from "./formFields";
import {
  mapFinancingProduct,
  mapSupport,
  mapBoolean,
  mapLoanKind,
  mapLoanFixedInterestRate
} from "./mappers";

import { type FinancingSectionProps } from "../../types/details";

const FinancingLoanEnterpriser = ({
  initialValues,
  handleSubmit,
  isLocked,
  site
}: FinancingSectionProps) => {
  return (
    <EditableForm initialValues={initialValues} onSubmit={handleSubmit} isLocked={isLocked}>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: FINANCING_SUPPORT,
            caption: () => "Unterstützung durch einen Experten"
          }}
          displayValue={mapSupport(site === "customer")}
        />
        <EditableField
          fieldControlProps={{
            name: FINANCING_PRODUCT,
            caption: () => "Finanzierungsart"
          }}
          displayValue={mapFinancingProduct}
        />
        <EditableField
          fieldControlProps={{
            name: LOAN_KIND,
            caption: () => "Darlehensart"
          }}
          displayValue={mapLoanKind}
        />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: AMOUNT,
            caption: () => "Investitionskosten"
          }}
          displayValue={formatters.formatCurrency}
        />
        <EditableField
          fieldControlProps={{
            name: FINANCING_OWN_FOUNDS,
            caption: () => "Eigenmittel"
          }}
          displayValue={formatters.formatCurrency}
        />
        <EditableField
          fieldControlProps={{
            name: FINANCING_DEMAND,
            caption: () => "Finanzierungsbedarf"
          }}
          displayValue={formatters.formatCurrency}
        />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: LOAN_TERM_IN_MONTHS,
            caption: () => "Laufzeit",
            validate: v.combineWithPrefillCheck(errors.requiredField())
          }}
          displayValue={formatters.formatMonths}
        />
        <ConditionalSubform
          condition={formConditions.fieldHasValue(LOAN_KIND, LOAN_KIND__ANNUITY_LOAN)}
          renderOnSatisfy={() => (
            <>
              <EditableField
                fieldControlProps={{
                  name: LOAN_INTEREST_PERIOD_IN_MONTHS,
                  caption: () => "Zinsfestschreibung"
                }}
                displayValue={formatters.formatMonths}
              />
              <div />
            </>
          )}
          renderOnFail={() => (
            <ConditionalSubform
              condition={formConditions.fieldHasTruthyValue(LOAN_KIND)}
              renderOnSatisfy={() => (
                <>
                  <EditableField
                    fieldControlProps={{
                      name: LOAN_FIXED_INTEREST_RATE,
                      caption: () => "Zinsbindungsart"
                    }}
                    displayValue={mapLoanFixedInterestRate}
                  />
                  <ConditionalSubform
                    condition={formConditions.fieldHasValue(
                      LOAN_FIXED_INTEREST_RATE,
                      LOAN_FIXED_INTEREST_RATE__FIXED
                    )}
                    renderOnSatisfy={() => (
                      <EditableField
                        fieldControlProps={{
                          name: LOAN_INTEREST_PERIOD_IN_MONTHS,
                          caption: () => "Zinsfestschreibung"
                        }}
                        displayValue={formatters.formatMonths}
                      />
                    )}
                    renderOnFail={() => <div />}
                  />
                </>
              )}
              renderOnFail={() => (
                <>
                  <div />
                  <div />
                </>
              )}
            />
          )}
        />
      </FormRow>
      <FormRow>
        <EditableField
          fieldControlProps={{
            name: CONSIDER_SUBSIDIES,
            caption: () =>
              "Bei der Anfrage sollen (sofern möglich) Fördermittel berücksichtigt werden."
          }}
          displayValue={mapBoolean}
        />
        <EditableField
          fieldControlProps={{
            name: LOAN_INTERESTED_IN_ALTERNATIVE_FINANCING,
            caption: () => "Kunde interessiert mich auch für eine Eigenkapitalstärkung"
          }}
          displayValue={mappers.yesNoMapper}
        />
        <div />
      </FormRow>
    </EditableForm>
  );
};

export default FinancingLoanEnterpriser;
